<template>
  <b-modal
    id="prescription-diagnostic-modal"
    ref="modal"
    title="Testar prescrição (diagnóstico)"
    size="md"
    hide-footer
    modal-class="dark-modal dark-modal--light"
    @shown="loadDoctors"
    @hidden="reset"
  >
    <div class="rxd">
      <p class="rxd__hint">
        Abre a tela de prescrição como se fosse o médico escolhido, para
        reproduzir o erro relatado. A receita é real — não imprima.
      </p>

      <label class="rxd__label" for="rxd-doctor">Médico</label>
      <select
        id="rxd-doctor"
        v-model="selectedDoctorId"
        class="rxd__select"
        :disabled="loadingDoctors || following"
        @change="onDoctorChange"
      >
        <option value="" disabled>
          {{ loadingDoctors ? "Carregando médicos…" : "Selecione um médico" }}
        </option>
        <option v-for="doctor in doctors" :key="doctor.id" :value="doctor.id">
          {{ doctor.name }} — {{ doctor.email }}
        </option>
      </select>

      <button
        v-if="!followed"
        type="button"
        class="btn btn--primary rxd__follow"
        :disabled="!selectedDoctorId || following"
        @click="follow"
      >
        {{ following ? "Carregando…" : "Seguir" }}
      </button>

      <p v-if="error" class="rxd__error">{{ error }}</p>

      <template v-if="followed">
        <p class="rxd__ok">
          Token de
          <strong>{{ selectedDoctorName }}</strong>
          carregado.
        </p>

        <label class="rxd__label" for="rxd-appt">Consulta</label>
        <select id="rxd-appt" v-model="selectedRoomId" class="rxd__select">
          <option value="" disabled>
            {{
              appointments.length
                ? "Selecione uma consulta"
                : "Nenhuma consulta com sala para este médico"
            }}
          </option>
          <option
            v-for="appt in appointments"
            :key="appt.room_id"
            :value="appt.room_id"
          >
            {{ appt.patient_name || "Paciente" }} —
            {{ formatDate(appt.scheduled_at) }}
          </option>
        </select>

        <!-- Mesmo ícone/ação que o médico vê na sala (title "Prescrição
             médica"): daqui em diante o comportamento é idêntico ao real. -->
        <div class="rxd__prescribe">
          <button
            type="button"
            class="rxd__rx-icon"
            title="Prescrição médica"
            :disabled="!selectedRoomId"
            @click="openPrescription"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 3h6a4 4 0 0 1 0 8H6V3zM6 11l9 10M6 11v10"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Prescrição médica
          </button>
        </div>
      </template>
    </div>
  </b-modal>
</template>

<script>
import { PrescriptionDiagnostics } from "../services/prescriptionDiagnostics";
import {
  stashDiagnosticToken,
  clearOpenerDiagnosticToken
} from "../utils/prescriptionDiagnostic";
import { formatFromENtoPT } from "@/utils/dates/format";

export default {
  name: "PrescriptionDiagnosticModal",
  data() {
    return {
      doctors: [],
      loadingDoctors: false,
      selectedDoctorId: "",
      following: false,
      followed: false,
      token: null,
      appointments: [],
      selectedRoomId: "",
      error: null
    };
  },
  methods: {
    async loadDoctors() {
      if (this.doctors.length) return;
      this.loadingDoctors = true;
      this.error = null;
      try {
        const { data } = await PrescriptionDiagnostics.listDoctors();
        this.doctors = data.doctors || [];
      } catch (error) {
        this.error = "Não foi possível carregar os médicos.";
      } finally {
        this.loadingDoctors = false;
      }
    },

    onDoctorChange() {
      // Trocar de médico invalida token e consultas já carregados.
      this.followed = false;
      this.token = null;
      this.appointments = [];
      this.selectedRoomId = "";
      this.error = null;
    },

    async follow() {
      this.following = true;
      this.error = null;
      try {
        const [tokenRes, apptRes] = await Promise.all([
          PrescriptionDiagnostics.token(this.selectedDoctorId),
          PrescriptionDiagnostics.listAppointments(this.selectedDoctorId)
        ]);
        this.token = tokenRes.data.memed_token;
        this.appointments = apptRes.data.appointments || [];
        this.followed = true;
      } catch (error) {
        const type =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.error_type;
        this.error =
          type === "memed_not_approved"
            ? "A Memed não aprova este médico — sem token para testar."
            : "Não foi possível carregar o token deste médico.";
      } finally {
        this.following = false;
      }
    },

    openPrescription() {
      if (!this.selectedRoomId || !this.token) return;

      // A aba nova herda uma CÓPIA do sessionStorage no instante do open.
      // Gravamos o token, abrimos e apagamos do abridor: só a cópia clonada
      // (na aba de prescrição) permanece, e ela se limpa ao ler.
      stashDiagnosticToken({
        token: this.token,
        doctorName: this.selectedDoctorName
      });

      const { href } = this.$router.resolve({
        name: "PrescriptionView",
        params: { room: this.selectedRoomId },
        query: { diagnostic: "1" }
      });
      // Sem "noopener": a aba precisa herdar o sessionStorage (auth + token).
      window.open(href, "_blank");

      clearOpenerDiagnosticToken();
      this.$refs.modal.hide();
    },

    formatDate(value) {
      if (!value) return "";
      return formatFromENtoPT(value.slice(0, 10));
    },

    reset() {
      this.selectedDoctorId = "";
      this.following = false;
      this.followed = false;
      this.token = null;
      this.appointments = [];
      this.selectedRoomId = "";
      this.error = null;
    }
  },
  computed: {
    selectedDoctorName() {
      const doctor = this.doctors.find(d => d.id === this.selectedDoctorId);
      return doctor ? doctor.name : "";
    }
  }
};
</script>

<style scoped>
.rxd {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.rxd__hint {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-muted, #6b7280);
}
.rxd__label {
  font-size: 13px;
  font-weight: 600;
  margin-bottom: -6px;
}
.rxd__select {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  font-size: 14px;
  background: #fff;
}
.rxd__follow {
  align-self: flex-start;
}
.rxd__ok {
  margin: 0;
  font-size: 13px;
  color: var(--color-success, #059669);
}
.rxd__error {
  margin: 0;
  font-size: 13px;
  color: var(--color-danger, #dc2626);
}
.rxd__prescribe {
  margin-top: 4px;
}
.rxd__rx-icon {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid var(--color-primary, #1a6cf7);
  color: var(--color-primary, #1a6cf7);
  background: transparent;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.rxd__rx-icon:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
